import { useState, useEffect } from "react"
import useLocalStorageState from "use-local-storage-state"
import { PipelineEditor } from "./PipelineEditor"
import { toast } from "sonner"
import { usePostRequest } from "../../Utils/hooks/QueryHandler"
import { DeleteRequest, GetRequest, PutRequest } from "../../Utils/hooks/ApiHandler"
import { Button, Alert, Modal, Form } from "react-bootstrap"
import Select from "react-select"
import DeleteModal from "../DeleteModal"
import { IconArrowLeftRight, IconArrowsLeftRight } from "@tabler/icons-react"
import { customStyles } from '../../Utils/helpers/commonhelper'
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials"
// Decode the encoded pipeline ID from URL parameter
const decodePipelineId = (encodedId) => {
  try {
    let base64 = encodedId.replace(/-/g, "+").replace(/_/g, "/")
    const padding = base64.length % 4
    if (padding) {
      base64 += "=".repeat(4 - padding)
    }
    const decodedStr = atob(base64)
    return Number.parseInt(decodedStr, 10)
  } catch (error) {
    return null
  }
}

// Encode pipeline ID for URL parameter
const encodePipelineId = (id) => {
  const base64 = btoa(id.toString())
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

export const EditPipelinePage = ({ encodedId, module_name, refetch, setEditPipeline, pipelineCount, refetchPipelineStage, pipelineGrant }) => {

  // extracting params from the url
  // const [searchParams] = useSearchParams();
  // const module_name = searchParams.get('module')
  // const { encodedId } = useParams()

  const { headers, extras, navigate, customerId, loginUserId, userType } = useGetEssentials()
  // Component state management
  const [pipelineNotFound, setPipelineNotFound] = useState(false)
  const [pipelineData, setPipelineData] = useState(null)
  const [pipelineStages, setPipelineStages] = useState(null)
  const [leadsData, setLeadsData] = useState([])
  // delete modal loading state
  const [loading, setLoading] = useState(false)

  // Lead transfer modal states
  const [showLeadTransferModal, setShowLeadTransferModal] = useState(false)
  const [targetStageId, setTargetStageId] = useState("")
  const [availableStagesForTransfer, setAvailableStagesForTransfer] = useState([])
  const [stageToDelete, setStageToDelete] = useState(null)
  const [showPipelineDeleteModal, setShowPipelineDeleteModal] = useState(false);

  const pipelineId = decodePipelineId(encodedId)

  // Lead transfer mutation hook
  const transferLeadsMutation = usePostRequest("lead/transferLeadsToNewStage/", false, { headers }, extras)


  const fetchPipelineById = async () => {
    try {
      const res = await GetRequest(`lead/fetchPipelineandStageById/${customerId}/${module_name}s/${pipelineId}`, { headers }, extras)
      if (res.status === "fail" || res.status === false) {
        setPipelineStages(null)
        setPipelineNotFound(true)
        return
      }
      setPipelineData(res.data[0])
      const formattedStages = res.data[0]?.stages.map((stage) => ({
        ...stage,
        sequence: String(stage.sequence),
        probability: Math.min(100, stage.probability || 0),
        rotting_days: stage.rotting_days === null ? null : stage.rotting_days,
        rotting_enabled: stage.rotting_days !== null && stage.rotting_days > 0,
      }))
      setPipelineStages(formattedStages)
      setPipelineNotFound(false)
    } catch (error) {
      return
    }
  }
  useEffect(() => {
    fetchPipelineById()
  }, [])
  // Set leads data from API response
  const fetchLeads = async () => {
    try {
      const res = await GetRequest(`lead/fetchLeads/${customerId}/${pipelineId}/${loginUserId}/${userType}/card`, { headers }, extras)
      if (res.status === false) {
        setLeadsData([])
        return
      }
      setLeadsData(res.data || [])
    } catch {
      return
    }
  }
  useEffect(() => {
    fetchLeads()
  }, [])

  const [save, setSave] = useState(false)
  // Save pipeline changes and refetch pipeline list
  const handleSavePipeline = async (updatedData) => {
    try {
      setSave(true)
      const stagesPayload = updatedData.pipeline_stage.map((stage) => ({
        stage_name: stage.stage_name,
        stage_id: stage.stage_id ? String(stage.stage_id) : "",
        sequence: stage.sequence,
        rotting_days: stage.rotting_days || null,
        probability: Math.min(100, stage.probability),
        color: stage.color || null, // Add color field
      }))
      const payload = {
        customer_id: customerId,
        pipeline_name: updatedData.pipeline_name,
        user_login_id: loginUserId,
        pipeline_stage: stagesPayload,
        pipeline_id: String(pipelineId),
      }

      const response = await PutRequest(`lead/updatePipelineStageSequence/${module_name}s`, payload, { headers }, extras)

      if (response?.status === "success") {
        toast.success(response.message || "Pipeline updated successfully")
        refetch()
        refetchPipelineStage()
        setSave(false)
        setEditPipeline(false)
      } else {
        setSave(false)
        toast.error(response?.message || "Failed to update pipeline")
      }
    } catch (error) {
      toast.error("An error occurred while saving pipeline")
      setSave(false)
      throw error
    }
  }

  // Navigate back to leads page
  const handleCancel = () => {
    refetch()
    refetchPipelineStage()
    setEditPipeline(false)
  }

  const [deleteStageConfirm, setDeleteStageConfirm] = useState(false)

  // Handle stage deletion with lead transfer check
  const handleStageDeleteRequest = async (stageId) => {

    const stage = pipelineStages.find((s) => s.stage_id === stageId || s.id === stageId)
    try {
      const response = await DeleteRequest(`lead/DeletePipelineStage/${stageId}/${customerId}/${loginUserId}`, { headers }, extras)
      if (response?.status === "success") {
        await fetchPipelineById()
        setDeleteStageConfirm(false)
        toast.success("Pipeline stage deleted successfully")

        // FIX: Use the stage ID that exists in the current state
        // The backend returns the deleted stage ID in the response
        const deletedStageId = stage.id

        setPipelineStages((prev) =>
          prev?.filter((s) => {
            // Check both possible ID fields
            const currentId = s.id
            const targetId = deletedStageId
            return currentId !== targetId
          })
        )

        // Also refresh leads data as stages have changed
        await fetchLeads()
        return true
      } else {
        // Check if stage contains leads that need to be transferred
        if (response?.message && response.message.toLowerCase().includes("lead")) {
          const availableStages = pipelineStages.filter((s) => s.id !== stage.id)
          setAvailableStagesForTransfer(availableStages)
          setTargetStageId("")
          setStageToDelete(stage)
          setShowLeadTransferModal(true)
          return false
        } else {
          toast.error(response?.message || "Failed to delete pipeline stage")
          return false
        }
      }
    } catch (error) {
      if (error.response?.data?.message && error.response.data.message.toLowerCase().includes("lead")) {
        const availableStages = pipelineStages.filter((s) => (s.stage_id || s.id) !== (stage.stage_id || stage.id))
        setAvailableStagesForTransfer(availableStages)
        setTargetStageId("")
        setStageToDelete(stage)
        setShowLeadTransferModal(true)
        return false
      } else {
        toast.error("Error deleting pipeline stage")
        return false;
      }
    }
  }

  // Transfer leads to new stage and delete the old stage
  const handleLeadTransferAndDelete = async () => {
    if (!targetStageId || !stageToDelete) {
      toast.error("Please select a target stage")
      return
    }

    try {
      const transferPayload = {
        old_stage_id: stageToDelete.id,
        new_stage_id: Number.parseInt(targetStageId),
        customer_id: customerId,
        user_login_id: loginUserId,
      }

      const transferResponse = await transferLeadsMutation.mutateAsync(transferPayload)

      if (transferResponse?.status === "success") {
        toast.success("Leads transferred successfully")

        // Delete the stage after successful lead transfer
        const deleteResponse = await DeleteRequest(
          `lead/DeletePipelineStage/${stageToDelete.stage_id || stageToDelete.id}/${customerId}/${loginUserId}`,
          { headers },
          extras,
        )

        if (deleteResponse?.status === "success") {
          await fetchPipelineById()
          setDeleteStageConfirm(false)
          toast.success("Pipeline stage deleted successfully")
          // FIX: Use consistent ID for filtering
          const deletedStageId = deleteResponse.data?.stage_id || stageToDelete.id || stageToDelete.stage_id

          setPipelineStages((prev) =>
            prev?.filter((s) => {
              const currentId = s.stage_id || s.id
              return currentId !== deletedStageId
            })
          )
        }

        // Close modal and reset state
        setShowLeadTransferModal(false)
        setStageToDelete(null)
        setTargetStageId("")

        // Refresh data
        await fetchLeads()
      } else {
        toast.error(transferResponse?.message || "Failed to transfer leads")
      }
    } catch (error) {
      toast.error("An error occurred while transferring leads")
    }
  }
  // Navigate to duplicate pipeline page with encoded pipeline ID
  const handleDuplicatePipeline = () => {
    if (pipelineId) {
      const encodedPipelineId = encodePipelineId(pipelineId)
      navigate(`/duplicatepipeline/${encodedPipelineId}`, {
        state: {
          module_name: `${module_name}s`
        }
      })
    }
  }

  const handleDeletePipeline = () => {
    if (!pipelineGrant.isDelete) {
      toast.warning("You are not authorized for this action!")
      return
    }
    setShowPipelineDeleteModal(true)
  }

  // Delete entire pipeline and navigate back
  const handleDeletePipelineConfirm = async () => {
    setLoading(true)
    try {
      const response = await DeleteRequest(`lead/deletePipeline/${pipelineId}/${customerId}/${loginUserId}/${module_name}s`, { headers }, extras)
      if (response?.status === "success") {
        setLoading(false)
        toast.success("Pipeline deleted successfully")
        // Refetch pipelines to update the list
        refetch()
        setEditPipeline(false)
      } else {
        setLoading(false)
        toast.error(response?.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error("An error occurred while deleting pipeline")
    }
  }

  // Close lead transfer modal and reset state
  const handleCloseLeadTransferModal = () => {
    setShowLeadTransferModal(false)
    setStageToDelete(null)
    setTargetStageId("")
  }

  if (pipelineNotFound) {
    return (
      <div className="edit-pipeline-page">
        <div className="container mt-5">
          <Alert variant="danger" className="text-center">
            <h4>Pipeline Not Found</h4>
            <p>The pipeline you're looking for doesn't exist or you don't have permission to access it.</p>
            <Button
              variant="primary"
              onClick={() => setEditPipeline(false)}
              style={{ backgroundColor: "#7a3a5e", borderColor: "#7a3a5e", textTransform: "capitalize" }}
            >
              Back to {module_name}s
            </Button>
          </Alert>
        </div>
      </div >
    )
  }


  // if (pipelineStages === null && pipelineData === null) {
  //   return (

  //   )
  // }

  return (
    <div>
      {
        pipelineStages !== null ?
          <PipelineEditor
            pipelineName={pipelineData?.pipeline_name}
            pipelineStage={pipelineStages}
            onClose={handleCancel}
            onSave={handleSavePipeline}
            onDuplicate={handleDuplicatePipeline}
            onDeletePipeline={handleDeletePipeline}
            onStageDeleteRequest={handleStageDeleteRequest}
            pipelineCount={pipelineCount}
            isCreatingNewPipeline={false}
            leadsData={leadsData}
            save={save}
            pipelineGrant={pipelineGrant}
            isCreate={false}
          // deleteStageConfirm={deleteStageConfirm}
          // setDeleteStageConfirm={setDeleteStageConfirm}
          />
          :
          <div className="d-flex justify-content-center align-items-center">
            <div className="container mt-5 text-center">
              <div className="spinner-border" role="status" style={{ color: "#7a3a5e" }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading pipeline...</p>
            </div>
          </div>
      }

      {/* Lead Transfer Modal - Properly organized in EditPipelinePage */}
      <Modal
        show={showLeadTransferModal}
        onHide={handleCloseLeadTransferModal}
        centered
        backdrop="static"
        contentClassName="border-0 shadow-lg rounded-3"
      >
        {/* Professional Utility Header */}
        <Modal.Header closeButton className="border-bottom-0 pt-4 px-4 align-items-start">
          <div className="d-flex flex-column">
            <div className="d-flex align-items-start mb-1 gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-2"
                style={{
                  background: "linear-gradient(135deg, #7a3a5e 0%, #4f243c 100%)",
                  width: "46px",
                  height: "36px",
                  boxShadow: "0 4px 10px rgba(122, 58, 94, 0.2)"
                }}
              >
                <IconArrowsLeftRight size={20} color="white" />
              </div>
              <div className="d-flex flex-column">
                <Modal.Title className="mb-0 fw-bold text-dark" style={{ fontSize: "0.95rem" }}>
                  Transfer Active Leads
                </Modal.Title>
                <small className="text-muted" style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                  This stage cannot be deleted while it contains data. Please reassign the leads below.
                </small>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="px-4 py-3 form-container">
          {/* Kept original Form.Group and Select logic exactly as requested */}
          <Form.Group className="mb-3 focus-z-index position-relative">
            <Form.Label className="d-block mb-1 ps-1 fw-bold text-muted"
              style={{ fontSize: "0.7rem", letterSpacing: "0.5px", textTransform: "uppercase" }}>SELECT TARGET STAGE</Form.Label>
            <Select
              styles={customStyles}
              classNamePrefix='react-select'
              value={
                availableStagesForTransfer
                  .map((stage) => ({
                    value: stage.stage_id || stage.id,
                    label: stage.stage_name,
                  }))
                  .find((option) => String(option.value) === String(targetStageId)) || null
              }
              onChange={(selectedOption) =>
                setTargetStageId(selectedOption ? selectedOption.value : "")
              }
              options={availableStagesForTransfer.map((stage) => ({
                value: stage.stage_id || stage.id,
                label: stage.stage_name,
              }))}
              placeholder="Choose a stage..."
              isClearable
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="border-top-0 px-4 pb-4 gap-2">
          <button
            onClick={handleCloseLeadTransferModal}
            className="text-decoration-none discard-btn px-3 py-2 fw-semibold "
            style={{
              backgroundColor: "#ffffff",
              color: "#7a3a5e",
              border: "1px solid #7a3a5e",
              fontSize: "14px",
              letterSpacing: "0.3px",
              borderRadius: "6px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleLeadTransferAndDelete}
            disabled={!targetStageId}
            className="save-pipeline-btn px-4 py-2 d-flex align-items-center gap-2 fw-semibold"
            style={{
              backgroundColor: !targetStageId ? "#a37a91" : "#7a3a5e", // Lighter plum when disabled for better intent
              color: "#fff",
              border: "none",
              fontSize: "14px", // Standard primary action size
              letterSpacing: "0.2px",
              borderRadius: "6px",
              boxShadow: "0 2px 4px rgba(122, 58, 94, 0.2)",
              cursor: !targetStageId ? "not-allowed" : "pointer",
              opacity: !targetStageId ? 0.7 : 1
            }}
          >
            Complete Transfer & Delete
          </button>
        </Modal.Footer>
      </Modal>
      <DeleteModal
        show={showPipelineDeleteModal}
        onClose={() => {
          setShowPipelineDeleteModal(false)
        }}
        onConfirm={handleDeletePipelineConfirm}
        isLoading={loading}
        message="Are you sure you want to permanently delete this pipeline?"
      />
    </div >
  )
}